import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';

const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    check: 'M20 6L9 17l-5-5',
    trash: ['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6', 'M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2', 'M10 11v6', 'M14 11v6'],
    edit: ['M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7', 'M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z'],
    plus: ['M12 5v14', 'M5 12h14'],
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    copy: ['M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'],
    info: ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 16v-4', 'M12 8h.01'],
    driver: ['M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2', 'M9 11a4 4 0 100-8 4 4 0 000 8z'],
    route: ['M4 19L9 4l5 15', 'M7 14h4', 'M18 8l3 3-3 3'],
    users: ['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 7a4 4 0 100-8 4 4 0 000 8z', 'M23 21v-2a4 4 0 00-3-3.87', 'M16 3.13a4 4 0 010 7.75'],
    fleet: ['M5 17H4a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-1', 'M7 17a2 2 0 100 4 2 2 0 000-4z', 'M17 17a2 2 0 100 4 2 2 0 000-4z'],
    passenger: ['M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2', 'M12 11a4 4 0 100-8 4 4 0 000 8z'],
    clients: ['M3 21h18', 'M5 21V7l8-4v18', 'M19 21V11l-6-4'],
    globe: ['M12 2a10 10 0 100 20 10 10 0 000-20z', 'M2 12h20', 'M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z'],
    sparkle: ['M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z']
};

const API = '/api';

// Pre-defined countries removed to use database source

function LimitItem({ icon, label, value }) {
    const isUnlimited = value === -1 || value === undefined || value === null;
    return (
        <div className="mpp-limit-item">
            <div className="mpp-limit-icon-wrap">
                <Icon d={icon} size={14} strokeWidth={2} />
            </div>
            <div className="mpp-limit-info">
                <span className="mpp-limit-title">{label}</span>
                <span className={`mpp-limit-val ${isUnlimited ? 'unlimited' : ''}`}>
                    {isUnlimited ? 'Unlimited' : value}
                </span>
            </div>
        </div>
    );
}

export default function ManagePricePlans({ onEdit, onCreate, user }) {
    const [plans, setPlans] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteId, setDeleteId] = useState(null);
    const [toast, setToast] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [isAllSelected, setIsAllSelected] = useState(true);
    const [showImportScreen, setShowImportScreen] = useState(false);
    const [duplicatePlan, setDuplicatePlan] = useState(null);
    const [duplicateName, setDuplicateName] = useState('');
    const [viewMode, setViewMode] = useState('cards');
    const [currencyDetailsPlan, setCurrencyDetailsPlan] = useState(null);

    const handleExport = () => {
        if (plans.length === 0) { alert('No plans to export.'); return; }
        const exportData = plans.map(p => ({
            'Plan Name': p.name || '',
            'Description': p.description || '',
            'Is Free': p.isFree ? 'Yes' : 'No',
            'Price': p.price || 0,
            'Price Period': p.pricePeriod || '',

            'Drivers Limit': p.driversLimit === -1 ? 'Unlimited' : (p.driversLimit || 0),
            'Routes Limit': p.routesLimit === -1 ? 'Unlimited' : (p.routesLimit || 0),
            'Users Limit': p.usersLimit === -1 ? 'Unlimited' : (p.usersLimit || 0),
            'Fleet Limit': p.fleetLimit === -1 ? 'Unlimited' : (p.fleetLimit || 0),
            'Passenger Limit': p.passengerLimit === -1 ? 'Unlimited' : (p.passengerLimit || 0),
            'Clients Limit': p.clientsLimit === -1 ? 'Unlimited' : (p.clientsLimit || 0),
            'Status': p.isActive ? 'Active' : 'Inactive'
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
            { wch: 18 }, { wch: 30 }, { wch: 10 },
            { wch: 10 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 10 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Price Plans');
        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Price_Plans_Export_${timestamp}.xlsx`);
    };

    const uniquePeriods = Array.from(new Set(plans.map(p => p.pricePeriod).filter(Boolean))).sort();

    useEffect(() => {
        if (!selectedPeriod && uniquePeriods.length > 0) {
            setSelectedPeriod(uniquePeriods[0]);
        }
    }, [uniquePeriods, selectedPeriod]);

    const filteredPlans = selectedPeriod
        ? plans.filter(p => p.pricePeriod === selectedPeriod)
        : plans;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchCountries = useCallback(async () => {
        try {
            const res = await fetch(`${API}/countries`);
            const data = await res.json();
            const activeCountries = (Array.isArray(data) ? data : []).filter(c => c.isActive);
            setCountries(activeCountries);
            if (activeCountries.length > 0 && !selectedCountry) {
                setSelectedCountry(activeCountries[0]);
            }
        } catch (err) {
            console.error("Fetch countries error:", err);
        }
    }, [selectedCountry]);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/price-plans`);
            const data = await res.json();
            setPlans(Array.isArray(data) ? data : []);
        } catch {
            setPlans([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchCountries();
        fetchPlans();
    }, [fetchCountries, fetchPlans]);

    const handleToggleActive = async (plan) => {
        try {
            const updated = { ...plan, isActive: !plan.isActive };
            const res = await fetch(`${API}/price-plans/${plan._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                },
                body: JSON.stringify(updated),
            });
            if (res.ok) {
                setPlans(prev => prev.map(p => p._id === plan._id ? updated : p));
                showToast(updated.isActive ? 'Plan activated' : 'Plan deactivated');
            }
        } catch {
            showToast('Failed to update status', 'error');
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await fetch(`${API}/price-plans/${id}`, {
                method: 'DELETE',
                headers: {
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                }
            });
            if (res.ok) {
                setPlans(prev => prev.filter(p => p._id !== id));
                showToast('Price plan deleted');
            } else {
                showToast('Failed to delete', 'error');
            }
        } catch {
            showToast('Server error', 'error');
        }
        setDeleteId(null);
    };

    const handleDuplicate = async () => {
        if (!duplicateName.trim()) {
            showToast('Please enter a new name', 'error');
            return;
        }
        try {
            const newPlanData = { ...duplicatePlan, name: duplicateName.trim() };
            delete newPlanData._id;
            delete newPlanData.createdAt;
            delete newPlanData.updatedAt;

            const res = await fetch(`${API}/price-plans`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                },
                body: JSON.stringify(newPlanData),
            });
            if (res.ok) {
                const created = await res.json();
                setPlans(prev => [created.plan, ...prev]);
                showToast('Plan duplicated successfully');
                setDuplicatePlan(null);
            } else {
                showToast('Failed to duplicate plan', 'error');
            }
        } catch {
            showToast('Server error', 'error');
        }
    };

    if (showImportScreen) {
        return (
            <PricePlanImportWizard
                plans={plans}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchPlans(); }}
            />
        );
    }

    return (
        <div className="mpp-page">
            {/* Toast */}
            {toast && (
                <div className={`pp-toast ${toast.type}`}>
                    {toast.type === 'error' ? (
                        <Icon d={['M18 6L6 18', 'M6 6l12 12']} size={14} strokeWidth={3} style={{ marginRight: 8 }} />
                    ) : (
                        <Icon d={icons.check} size={14} strokeWidth={3} style={{ marginRight: 8 }} />
                    )}
                    {toast.msg}
                </div>
            )}

            {/* Delete confirm Modal */}
            {deleteId && (
                <div className="mpp-modal-overlay">
                    <div className="mpp-modal-box">
                        <div className="mpp-modal-icon">
                            <Icon d={icons.trash} size={42} color="#dd3232" strokeWidth={1.5} />
                        </div>
                        <h3 className="mpp-modal-title">Delete Price Plan?</h3>
                        <p className="mpp-modal-sub">This action cannot be undone.</p>
                        <div className="mpp-modal-actions">
                            <button className="mpp-btn-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
                            <button className="mpp-btn-confirm" onClick={() => handleDelete(deleteId)}>Yes, Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Duplicate Modal */}
            {duplicatePlan && (
                <div className="mpp-modal-overlay">
                    <div className="mpp-modal-box">
                        <h3 className="mpp-modal-title">Duplicate Plan</h3>
                        <p className="mpp-modal-sub">Enter a new name for the duplicated plan:</p>
                        <input 
                            type="text" 
                            style={{ width: '100%', marginBottom: '20px', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                            value={duplicateName}
                            onChange={(e) => setDuplicateName(e.target.value)}
                            placeholder="New Plan Name"
                        />
                        <div className="mpp-modal-actions">
                            <button className="mpp-btn-cancel" onClick={() => setDuplicatePlan(null)}>Cancel</button>
                            <button className="mpp-btn-confirm" style={{ background: '#2563eb' }} onClick={handleDuplicate}>Duplicate</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Currency Details Modal */}
            {currencyDetailsPlan && (
                <div className="mpp-modal-overlay">
                    <div className="mpp-modal-box" style={{ maxWidth: '480px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                            <div>
                                <h3 className="mpp-modal-title" style={{ margin: 0, textAlign: 'left', fontSize: 18 }}>{currencyDetailsPlan.name}</h3>
                                <p style={{ margin: '3px 0 0', fontSize: 12.5, color: '#64748b', textAlign: 'left' }}>Country-specific conversion rates</p>
                            </div>
                            <button 
                                onClick={() => setCurrencyDetailsPlan(null)} 
                                style={{ border: 'none', background: '#f1f5f9', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 18 }}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="mpp-all-currencies" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                            {countries.map(c => {
                                const locale = c.locale || 'en-US';
                                const rate = c.exchangeRate || c.rate || 1;
                                const safeLocale = `${locale.split('-u-')[0]}-u-nu-latn`;
                                const displayCurrency = c.currency || '';
                                const specificPriceObj = currencyDetailsPlan.prices?.find(p => p.country === c.name);
                                const finalAmount = specificPriceObj && specificPriceObj.amount != null 
                                    ? specificPriceObj.amount 
                                    : (currencyDetailsPlan.price * rate);
                                return (
                                    <div key={c.name} className="mpp-currency-row">
                                        <span className="mpp-cur-label">{c.name}</span>
                                        <span className="mpp-cur-val">
                                            {displayCurrency} {finalAmount.toLocaleString(safeLocale, { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mpp-modal-actions" style={{ marginTop: '20px' }}>
                            <button className="mpp-btn-cancel" style={{ width: '100%' }} onClick={() => setCurrencyDetailsPlan(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modern Superadmin Header */}
            <div className="mpp-header-bar">
                <div className="mpp-header-left">
                    <div className="mpp-badge-tag">
                        <Icon d={icons.sparkle} size={13} strokeWidth={2.2} />
                        Subscription Engine
                    </div>
                    <div className="mpp-header-title-row">
                        <h1 className="mpp-page-title">Price Plans</h1>
                        <span className="mpp-plan-count-badge">{plans.length} Total Plans</span>
                    </div>
                    <p className="mpp-page-subtitle">
                        Configure pricing tiers, country-specific currency overrides, and resource allocation limits.
                    </p>
                </div>

                <div className="mpp-header-actions-bar">
                    {/* Country Selector */}
                    <div className="mpp-filter-group">
                        <div className="mpp-filter-icon">
                            <Icon d={icons.globe} size={15} strokeWidth={2} />
                        </div>
                        <select
                            className="mpp-country-select"
                            value={isAllSelected ? 'All' : selectedCountry?.name || ''}
                            onChange={(e) => {
                                if (e.target.value === 'All') {
                                    setIsAllSelected(true);
                                } else {
                                    setIsAllSelected(false);
                                    const country = countries.find(c => c.name === e.target.value);
                                    setSelectedCountry(country);
                                }
                            }}
                        >
                            <option value="All">All Countries (Overview)</option>
                            {countries.map(c => (
                                <option key={c.name} value={c.name}>{c.name} ({c.currency || ''})</option>
                            ))}
                        </select>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="mpp-view-switcher">
                        <button 
                            className={`mpp-view-pill ${viewMode === 'cards' ? 'active' : ''}`}
                            onClick={() => setViewMode('cards')}
                            title="Cards View"
                        >
                            Cards
                        </button>
                        <button 
                            className={`mpp-view-pill ${viewMode === 'table' ? 'active' : ''}`}
                            onClick={() => setViewMode('table')}
                            title="Table View"
                        >
                            Table
                        </button>
                    </div>

                    <div className="mpp-header-buttons">
                        <button
                            onClick={() => setShowImportScreen(true)}
                            className="mpp-btn-secondary"
                        >
                            <Icon d={icons.upload} size={15} strokeWidth={2.2} />
                            Import
                        </button>
                        <button
                            onClick={handleExport}
                            className="mpp-btn-secondary"
                        >
                            <Icon d={icons.download} size={15} strokeWidth={2.2} />
                            Export
                        </button>
                        {(user?.userType === 'admin' || user?.permissions?.includes('Create Price Plan')) && onCreate && (
                            <button className="mpp-btn-primary" onClick={onCreate}>
                                <Icon d={icons.plus} size={16} strokeWidth={2.5} />
                                Create Plan
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Content Container */}
            <div className="mpp-main-content">
                {loading ? (
                    <div className="mpp-loading-state">
                        <div className="mpp-spinner" />
                        <span>Loading pricing plans...</span>
                    </div>
                ) : plans.length === 0 ? (
                    <div className="mpp-empty-state">
                        <div className="mpp-empty-icon">
                            <Icon d={icons.sparkle} size={36} strokeWidth={1.5} />
                        </div>
                        <h3>No Price Plans Created Yet</h3>
                        <p>Get started by creating your first tier with custom pricing and quotas.</p>
                        {(user?.userType === 'admin' || user?.permissions?.includes('Create Price Plan')) && onCreate && (
                            <button className="mpp-btn-primary" onClick={onCreate} style={{ marginTop: 14 }}>
                                <Icon d={icons.plus} size={16} strokeWidth={2.5} />
                                Create Your First Plan
                            </button>
                        )}
                    </div>
                ) : viewMode === 'table' ? (
                    <div className="mpp-table-wrapper">
                        <table className="mpp-modern-table">
                            <thead>
                                <tr>
                                    <th>Plan Details</th>
                                    <th>Pricing</th>
                                    <th>Resource Limits</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {plans.map((plan) => (
                                    <tr key={plan._id}>
                                        <td>
                                            <div className="mpp-table-plan-cell">
                                                <span className="mpp-table-plan-name">{plan.name}</span>
                                                <span className="mpp-table-plan-desc">{plan.description || (plan.isFree ? 'Free tier access' : `${plan.pricePeriod || 'Monthly'} subscription`)}</span>
                                            </div>
                                        </td>
                                        <td>
                                            {isAllSelected ? (
                                                <div className="mpp-table-price-multi">
                                                    <span>Multi-Country</span>
                                                    <button 
                                                        className="mpp-table-info-btn"
                                                        onClick={() => setCurrencyDetailsPlan(plan)}
                                                        title="View all country rates"
                                                    >
                                                        Rates <Icon d={icons.info} size={14} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="mpp-table-price-val">
                                                    <span className="mpp-t-cur">{selectedCountry?.currency || '$'}</span>
                                                    {(() => {
                                                        if (plan.price == null || !selectedCountry) return '0.00';
                                                        const specificPriceObj = plan.prices?.find(p => p.country === selectedCountry.name);
                                                        const finalAmount = specificPriceObj && specificPriceObj.amount != null 
                                                            ? specificPriceObj.amount 
                                                            : (plan.price * (selectedCountry.exchangeRate || selectedCountry.rate || 1));
                                                        return finalAmount.toLocaleString(
                                                            `${(selectedCountry.locale || 'en-US').split('-u-')[0]}-u-nu-latn`,
                                                            { minimumFractionDigits: 2 }
                                                        );
                                                    })()}
                                                    <span className="mpp-t-period">/{plan.pricePeriod || 'mo'}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="mpp-table-limits-inline">
                                                <span className="mpp-mini-tag"><strong>{plan.driversLimit === -1 ? '∞' : plan.driversLimit}</strong> Drivers</span>
                                                <span className="mpp-mini-tag"><strong>{plan.routesLimit === -1 ? '∞' : plan.routesLimit}</strong> Routes</span>
                                                <span className="mpp-mini-tag"><strong>{plan.usersLimit === -1 ? '∞' : plan.usersLimit}</strong> Users</span>
                                                <span className="mpp-mini-tag"><strong>{plan.fleetLimit === -1 ? '∞' : plan.fleetLimit}</strong> Fleet</span>
                                                <span className="mpp-mini-tag"><strong>{plan.passengerLimit === -1 ? '∞' : plan.passengerLimit}</strong> Pass.</span>
                                                <span className="mpp-mini-tag"><strong>{plan.clientsLimit === -1 ? '∞' : plan.clientsLimit}</strong> Clients</span>
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                className={`mpp-status-pill ${plan.isActive ? 'active' : 'inactive'}`}
                                                onClick={() => handleToggleActive(plan)}
                                                title={`Click to ${plan.isActive ? 'deactivate' : 'activate'}`}
                                            >
                                                <span className="mpp-status-dot" />
                                                {plan.isActive ? 'Active' : 'Inactive'}
                                            </button>
                                        </td>
                                        <td>
                                            <div className="mpp-actions-group">
                                                {(user?.userType === 'admin' || user?.permissions?.includes('Edit Price Plan')) && (
                                                    <button className="mpp-btn-icon" title="Edit Plan" onClick={async () => {
                                                        try {
                                                            const res = await fetch(`/api/price-plans/${plan._id}/check-subscriptions`);
                                                            const data = await res.json();
                                                            if (data.hasActive) {
                                                                showToast('Cannot edit this plan because it has active subscriptions.', 'error');
                                                            } else {
                                                                onEdit(plan);
                                                            }
                                                        } catch (e) {
                                                            showToast('Error checking plan status', 'error');
                                                        }
                                                    }}>
                                                        <Icon d={icons.edit} size={15} />
                                                    </button>
                                                )}
                                                {(user?.userType === 'admin' || user?.permissions?.includes('Create Price Plan')) && (
                                                    <button className="mpp-btn-icon" title="Duplicate Plan" onClick={() => {
                                                        setDuplicatePlan(plan);
                                                        setDuplicateName(`${plan.name} (Copy)`);
                                                    }}>
                                                        <Icon d={icons.copy} size={15} />
                                                    </button>
                                                )}
                                                {(user?.userType === 'admin' || user?.permissions?.includes('Delete Price Plan')) && (
                                                    <button className="mpp-btn-icon delete" title="Delete Plan" onClick={() => setDeleteId(plan._id)}>
                                                        <Icon d={icons.trash} size={15} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="mpp-grid">
                        {plans.map((plan) => (
                            <div key={plan._id} className={`mpp-card ${!plan.isActive ? 'plan-inactive' : ''}`}>
                                {/* Card Top Row: Name + Badges + Actions */}
                                <div className="mpp-card-header">
                                    <div className="mpp-card-top-row">
                                        <button
                                            className={`mpp-status-pill ${plan.isActive ? 'active' : 'inactive'}`}
                                            onClick={() => handleToggleActive(plan)}
                                            title={`Click to ${plan.isActive ? 'deactivate' : 'activate'}`}
                                        >
                                            <span className="mpp-status-dot" />
                                            {plan.isActive ? 'Active' : 'Inactive'}
                                        </button>

                                        <div className="mpp-actions-group">
                                            {(user?.userType === 'admin' || user?.permissions?.includes('Edit Price Plan')) && (
                                                <button className="mpp-btn-icon" title="Edit Plan" onClick={async () => {
                                                    try {
                                                        const res = await fetch(`/api/price-plans/${plan._id}/check-subscriptions`);
                                                        const data = await res.json();
                                                        if (data.hasActive) {
                                                            showToast('Cannot edit this plan because it has active subscriptions.', 'error');
                                                        } else {
                                                            onEdit(plan);
                                                        }
                                                    } catch (e) {
                                                        showToast('Error checking plan status', 'error');
                                                    }
                                                }}>
                                                    <Icon d={icons.edit} size={14} />
                                                </button>
                                            )}
                                            {(user?.userType === 'admin' || user?.permissions?.includes('Create Price Plan')) && (
                                                <button className="mpp-btn-icon" title="Duplicate Plan" onClick={() => {
                                                    setDuplicatePlan(plan);
                                                    setDuplicateName(`${plan.name} (Copy)`);
                                                }}>
                                                    <Icon d={icons.copy} size={14} />
                                                </button>
                                            )}
                                            {(user?.userType === 'admin' || user?.permissions?.includes('Delete Price Plan')) && (
                                                <button className="mpp-btn-icon delete" title="Delete Plan" onClick={() => setDeleteId(plan._id)}>
                                                    <Icon d={icons.trash} size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <h2 className="mpp-plan-name">{plan.name}</h2>
                                    <p className="mpp-plan-desc">
                                        {plan.description || (plan.isFree ? 'Free tier access with basic limits' : `Full access subscription billed ${plan.pricePeriod || 'monthly'}`)}
                                    </p>
                                </div>

                                {/* Card Price Hero Section */}
                                <div className="mpp-price-section">
                                    {isAllSelected ? (
                                        <div className="mpp-all-countries-preview">
                                            <div className="mpp-all-countries-header">
                                                <span className="mpp-overview-tag">Multi-Country Pricing</span>
                                                <button
                                                    className="mpp-view-rates-btn"
                                                    onClick={() => setCurrencyDetailsPlan(plan)}
                                                >
                                                    View All ({countries.length})
                                                </button>
                                            </div>
                                            <div className="mpp-country-chips-row">
                                                {countries.slice(0, 3).map(c => {
                                                    const locale = c.locale || 'en-US';
                                                    const rate = c.exchangeRate || c.rate || 1;
                                                    const safeLocale = `${locale.split('-u-')[0]}-u-nu-latn`;
                                                    const displayCurrency = c.currency || '';
                                                    const specificPriceObj = plan.prices?.find(p => p.country === c.name);
                                                    const finalAmount = specificPriceObj && specificPriceObj.amount != null 
                                                        ? specificPriceObj.amount 
                                                        : (plan.price * rate);
                                                    return (
                                                        <div key={c.name} className="mpp-country-chip">
                                                            <span className="mpp-chip-country">{c.name.substring(0, 3).toUpperCase()}</span>
                                                            <span className="mpp-chip-val">{displayCurrency} {finalAmount.toLocaleString(safeLocale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                                                        </div>
                                                    );
                                                })}
                                                {countries.length > 3 && (
                                                    <span className="mpp-chip-more" onClick={() => setCurrencyDetailsPlan(plan)}>
                                                        +{countries.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mpp-hero-price">
                                            <span className="mpp-currency-symbol">{selectedCountry?.currency || '$'}</span>
                                            <span className="mpp-amount-text">
                                                {(() => {
                                                    if (plan.price == null || !selectedCountry) return '0.00';
                                                    const specificPriceObj = plan.prices?.find(p => p.country === selectedCountry.name);
                                                    const finalAmount = specificPriceObj && specificPriceObj.amount != null 
                                                        ? specificPriceObj.amount 
                                                        : (plan.price * (selectedCountry.exchangeRate || selectedCountry.rate || 1));
                                                    return finalAmount.toLocaleString(
                                                        `${(selectedCountry.locale || 'en-US').split('-u-')[0]}-u-nu-latn`,
                                                        { minimumFractionDigits: 2 }
                                                    );
                                                })()}
                                            </span>
                                            <span className="mpp-billing-interval">/{plan.pricePeriod || 'month'}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Limits Grid */}
                                <div className="mpp-card-body">
                                    <div className="mpp-limits-header">
                                        <span className="mpp-limits-title">Resource Allocations</span>
                                    </div>
                                    <div className="mpp-limits-grid">
                                        <LimitItem icon={icons.driver} label="Drivers" value={plan.driversLimit} />
                                        <LimitItem icon={icons.route} label="Routes" value={plan.routesLimit} />
                                        <LimitItem icon={icons.users} label="Users" value={plan.usersLimit} />
                                        <LimitItem icon={icons.fleet} label="Fleet Vehicles" value={plan.fleetLimit} />
                                        <LimitItem icon={icons.passenger} label="Passengers" value={plan.passengerLimit} />
                                        <LimitItem icon={icons.clients} label="Clients" value={plan.clientsLimit} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                .mpp-page {
                    padding: 28px 32px 48px;
                    background: #f8fafc;
                    min-height: 100vh;
                    font-family: 'Outfit', 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                }

                /* Modern Navy Blue Header Bar */
                .mpp-header-bar {
                    background: linear-gradient(135deg, #0b1e3b 0%, #112d59 55%, #183d73 100%);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 18px;
                    padding: 24px 30px;
                    margin-bottom: 28px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    box-shadow: 0 12px 30px -6px rgba(11, 30, 59, 0.35), 0 4px 10px -2px rgba(11, 30, 59, 0.2);
                    gap: 20px;
                    flex-wrap: wrap;
                    position: relative;
                    overflow: hidden;
                }

                .mpp-header-bar::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: radial-gradient(circle at 90% 10%, rgba(59, 130, 246, 0.25) 0%, transparent 60%);
                    pointer-events: none;
                }

                .mpp-header-left {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    position: relative;
                    z-index: 1;
                }

                .mpp-badge-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 12px;
                    border-radius: 9999px;
                    background: rgba(255, 255, 255, 0.12);
                    backdrop-filter: blur(8px);
                    color: #93c5fd;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 0.5px;
                    width: fit-content;
                    text-transform: uppercase;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                }

                .mpp-header-title-row {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .mpp-page-title {
                    margin: 0;
                    font-size: 26px;
                    font-weight: 800;
                    color: #ffffff;
                    letter-spacing: -0.5px;
                    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }

                .mpp-plan-count-badge {
                    background: rgba(255, 255, 255, 0.15);
                    color: #e2e8f0;
                    font-size: 12px;
                    font-weight: 700;
                    padding: 3px 10px;
                    border-radius: 8px;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                .mpp-page-subtitle {
                    margin: 0;
                    font-size: 13.5px;
                    color: #cbd5e1;
                    max-width: 600px;
                }

                .mpp-header-actions-bar {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                    position: relative;
                    z-index: 1;
                }

                /* Country Selector */
                .mpp-filter-group {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .mpp-filter-icon {
                    position: absolute;
                    left: 12px;
                    color: #94a3b8;
                    pointer-events: none;
                    display: flex;
                    align-items: center;
                }

                .mpp-country-select {
                    height: 40px;
                    padding: 0 16px 0 34px;
                    border-radius: 10px;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    background: rgba(255, 255, 255, 0.95);
                    color: #0f172a;
                    font-size: 13.5px;
                    font-weight: 700;
                    outline: none;
                    cursor: pointer;
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                    transition: all 0.15s ease;
                }

                .mpp-country-select:hover {
                    background: #ffffff;
                    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
                }

                /* View Switcher */
                .mpp-view-switcher {
                    display: flex;
                    background: rgba(0, 0, 0, 0.25);
                    padding: 3px;
                    border-radius: 10px;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                }

                .mpp-view-pill {
                    padding: 6px 14px;
                    border-radius: 8px;
                    border: none;
                    background: transparent;
                    font-size: 13px;
                    font-weight: 700;
                    color: #cbd5e1;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }

                .mpp-view-pill.active {
                    background: #ffffff;
                    color: #0b1e3b;
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
                }

                .mpp-header-buttons {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .mpp-btn-secondary {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    height: 40px;
                    padding: 0 15px;
                    border-radius: 10px;
                    border: 1px solid rgba(255, 255, 255, 0.25);
                    background: rgba(255, 255, 255, 0.12);
                    backdrop-filter: blur(6px);
                    color: #ffffff;
                    font-size: 13.5px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }

                .mpp-btn-secondary:hover {
                    background: rgba(255, 255, 255, 0.22);
                    border-color: rgba(255, 255, 255, 0.4);
                    transform: translateY(-1px);
                }

                .mpp-btn-primary {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    height: 40px;
                    padding: 0 18px;
                    border-radius: 10px;
                    border: none;
                    background: #ffffff;
                    color: #0b1e3b;
                    font-size: 13.5px;
                    font-weight: 800;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                    transition: all 0.15s ease;
                }

                .mpp-btn-primary:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
                }

                /* Content Area */
                .mpp-main-content {
                    width: 100%;
                }

                /* Loading & Empty States */
                .mpp-loading-state, .mpp-empty-state {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    padding: 60px 20px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }

                .mpp-spinner {
                    width: 36px;
                    height: 36px;
                    border: 3px solid #e2e8f0;
                    border-top-color: #2563eb;
                    border-radius: 50%;
                    animation: mpp-spin 0.8s linear infinite;
                    margin-bottom: 16px;
                }

                @keyframes mpp-spin {
                    to { transform: rotate(360deg); }
                }

                .mpp-empty-icon {
                    width: 64px;
                    height: 64px;
                    border-radius: 16px;
                    background: #eff6ff;
                    color: #2563eb;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 16px;
                }

                .mpp-empty-state h3 {
                    margin: 0 0 6px;
                    font-size: 18px;
                    font-weight: 700;
                    color: #0f172a;
                }

                .mpp-empty-state p {
                    margin: 0;
                    font-size: 14px;
                    color: #64748b;
                }

                /* Cards Grid */
                .mpp-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 24px;
                }

                @media (max-width: 1280px) {
                    .mpp-grid { grid-template-columns: repeat(2, 1fr); }
                }

                @media (max-width: 768px) {
                    .mpp-grid { grid-template-columns: 1fr; }
                    .mpp-header-bar { flex-direction: column; align-items: flex-start; }
                }

                /* Modern Card Style */
                .mpp-card {
                    background: #ffffff;
                    border-radius: 18px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 14px -2px rgba(15, 23, 42, 0.04), 0 2px 6px -2px rgba(15, 23, 42, 0.02);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                }

                .mpp-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 24px -4px rgba(15, 23, 42, 0.08), 0 4px 8px -2px rgba(15, 23, 42, 0.03);
                    border-color: #cbd5e1;
                }

                .mpp-card.plan-inactive {
                    opacity: 0.78;
                    background: #fbfcfe;
                }

                /* Card Header */
                .mpp-card-header {
                    padding: 22px 24px 18px;
                    border-bottom: 1px solid #f1f5f9;
                    background: #ffffff;
                }

                .mpp-card-top-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 14px;
                }

                /* Status Pill */
                .mpp-status-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 10px;
                    border-radius: 9999px;
                    font-size: 11.5px;
                    font-weight: 700;
                    border: 1px solid transparent;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }

                .mpp-status-pill.active {
                    background: #ecfdf5;
                    color: #059669;
                    border-color: #a7f3d0;
                }

                .mpp-status-pill.inactive {
                    background: #f1f5f9;
                    color: #64748b;
                    border-color: #e2e8f0;
                }

                .mpp-status-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: currentColor;
                }

                /* Action Icon Buttons */
                .mpp-actions-group {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .mpp-btn-icon {
                    width: 30px;
                    height: 30px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: #ffffff;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    padding: 0;
                }

                .mpp-btn-icon:hover {
                    background: #f8fafc;
                    color: #2563eb;
                    border-color: #cbd5e1;
                }

                .mpp-btn-icon.delete:hover {
                    background: #fef2f2;
                    color: #ef4444;
                    border-color: #fecaca;
                }

                .mpp-plan-name {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 800;
                    color: #0f172a;
                    letter-spacing: -0.3px;
                }

                .mpp-plan-desc {
                    margin: 6px 0 0;
                    font-size: 12.5px;
                    color: #64748b;
                    line-height: 1.4;
                    min-height: 35px;
                }

                /* Price Section */
                .mpp-price-section {
                    padding: 20px 24px;
                    background: #f8fafc;
                    border-bottom: 1px solid #f1f5f9;
                }

                .mpp-hero-price {
                    display: flex;
                    align-items: baseline;
                    gap: 3px;
                }

                .mpp-currency-symbol {
                    font-size: 20px;
                    font-weight: 700;
                    color: #2563eb;
                }

                .mpp-amount-text {
                    font-size: 36px;
                    font-weight: 800;
                    color: #0f172a;
                    letter-spacing: -1px;
                    line-height: 1;
                }

                .mpp-billing-interval {
                    font-size: 13.5px;
                    font-weight: 600;
                    color: #64748b;
                    margin-left: 2px;
                }

                /* Multi-Country Preview */
                .mpp-all-countries-preview {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .mpp-all-countries-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .mpp-overview-tag {
                    font-size: 12px;
                    font-weight: 700;
                    color: #475569;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }

                .mpp-view-rates-btn {
                    background: transparent;
                    border: none;
                    color: #2563eb;
                    font-size: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    padding: 0;
                    transition: all 0.15s ease;
                }

                .mpp-view-rates-btn:hover {
                    text-decoration: underline;
                    color: #1d4ed8;
                }

                .mpp-country-chips-row {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .mpp-country-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    padding: 4px 8px;
                    border-radius: 6px;
                    font-size: 11.5px;
                }

                .mpp-chip-country {
                    font-weight: 800;
                    color: #64748b;
                }

                .mpp-chip-val {
                    font-weight: 700;
                    color: #0f172a;
                }

                .mpp-chip-more {
                    font-size: 11.5px;
                    font-weight: 700;
                    color: #2563eb;
                    cursor: pointer;
                    background: #eff6ff;
                    padding: 4px 8px;
                    border-radius: 6px;
                }

                /* Limits Section */
                .mpp-card-body {
                    padding: 20px 24px 24px;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }

                .mpp-limits-header {
                    margin-bottom: 12px;
                }

                .mpp-limits-title {
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.6px;
                    color: #94a3b8;
                }

                .mpp-limits-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 10px;
                }

                .mpp-limit-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 12px;
                    border-radius: 10px;
                    background: #f8fafc;
                    border: 1px solid #f1f5f9;
                    transition: background 0.15s ease;
                }

                .mpp-limit-item:hover {
                    background: #f1f5f9;
                }

                .mpp-limit-icon-wrap {
                    width: 28px;
                    height: 28px;
                    border-radius: 7px;
                    background: #eff6ff;
                    color: #2563eb;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .mpp-limit-info {
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                }

                .mpp-limit-title {
                    font-size: 11.5px;
                    font-weight: 600;
                    color: #64748b;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .mpp-limit-val {
                    font-size: 13px;
                    font-weight: 800;
                    color: #0f172a;
                    white-space: nowrap;
                }

                .mpp-limit-val.unlimited {
                    color: #059669;
                }

                /* Modern Table Styling */
                .mpp-table-wrapper {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 12px -2px rgba(15, 23, 42, 0.04);
                    overflow: hidden;
                }

                .mpp-modern-table {
                    width: 100%;
                    border-collapse: collapse;
                    text-align: left;
                }

                .mpp-modern-table th {
                    background: #f8fafc;
                    padding: 14px 20px;
                    font-size: 12px;
                    font-weight: 700;
                    color: #475569;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border-bottom: 1px solid #e2e8f0;
                }

                .mpp-modern-table td {
                    padding: 16px 20px;
                    border-bottom: 1px solid #f1f5f9;
                    color: #1e293b;
                    font-size: 13.5px;
                    vertical-align: middle;
                }

                .mpp-modern-table tr:hover td {
                    background: #f8fafc;
                }

                .mpp-table-plan-cell {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }

                .mpp-table-plan-name {
                    font-size: 14.5px;
                    font-weight: 800;
                    color: #0f172a;
                }

                .mpp-table-plan-desc {
                    font-size: 12px;
                    color: #64748b;
                }

                .mpp-table-price-val {
                    font-weight: 800;
                    font-size: 16px;
                    color: #0f172a;
                    display: flex;
                    align-items: baseline;
                    gap: 2px;
                }

                .mpp-t-cur {
                    color: #2563eb;
                    font-size: 13.5px;
                }

                .mpp-t-period {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 500;
                }

                .mpp-table-price-multi {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    color: #475569;
                }

                .mpp-table-info-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    background: #eff6ff;
                    color: #2563eb;
                    border: 1px solid #bfdbfe;
                    padding: 3px 8px;
                    border-radius: 6px;
                    font-size: 11.5px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }

                .mpp-table-info-btn:hover {
                    background: #dbeafe;
                }

                .mpp-table-limits-inline {
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .mpp-mini-tag {
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    padding: 3px 8px;
                    border-radius: 6px;
                    font-size: 11.5px;
                    color: #475569;
                }

                .mpp-mini-tag strong {
                    color: #0f172a;
                    font-weight: 800;
                }

                /* Modals & Dialogs */
                .mpp-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                }

                .mpp-modal-box {
                    background: #ffffff;
                    border-radius: 18px;
                    padding: 28px;
                    max-width: 440px;
                    width: 90%;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    border: 1px solid #e2e8f0;
                }

                .mpp-modal-icon {
                    width: 52px;
                    height: 52px;
                    border-radius: 14px;
                    background: #fef2f2;
                    color: #ef4444;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px;
                }

                .mpp-modal-title {
                    margin: 0 0 6px;
                    font-size: 19px;
                    font-weight: 800;
                    color: #0f172a;
                    text-align: center;
                }

                .mpp-modal-sub {
                    margin: 0 0 20px;
                    font-size: 13.5px;
                    color: #64748b;
                    text-align: center;
                }

                .mpp-modal-actions {
                    display: flex;
                    gap: 10px;
                    justify-content: flex-end;
                }

                .mpp-btn-cancel {
                    padding: 9px 18px;
                    border-radius: 10px;
                    border: 1px solid #cbd5e1;
                    background: #ffffff;
                    color: #475569;
                    font-size: 13.5px;
                    font-weight: 600;
                    cursor: pointer;
                }

                .mpp-btn-confirm {
                    padding: 9px 18px;
                    border-radius: 10px;
                    border: none;
                    background: #ef4444;
                    color: #ffffff;
                    font-size: 13.5px;
                    font-weight: 700;
                    cursor: pointer;
                }

                /* Currency Multi-Pricing List */
                .mpp-all-currencies {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    padding-right: 6px;
                }

                .mpp-currency-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 12px;
                    background: #f8fafc;
                    border-radius: 8px;
                    font-size: 13px;
                }

                .mpp-cur-label {
                    color: #475569;
                    font-weight: 600;
                }

                .mpp-cur-val {
                    color: #0f172a;
                    font-weight: 800;
                }
            `}</style>
        </div >
    );
}

function PricePlanImportWizard({ plans, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    // Map of existing plans (lowercase name -> true)
    const existingNames = new Set(plans.map(p => (p.name || '').trim().toLowerCase()));

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                // Standardize headers
                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Plan Name': 'Starter',
                'Description': 'Basic plan for small teams',
                'Is Free': 'No',
                'Price': 200,
                'Price Period': 'monthly',
                'Vehicles Limit': 10,
                'Drivers Limit': 9,
                'Routes Limit': 8,
                'Users Limit': 7,
                'Passenger Limit': 50,
                'Clients Limit': 5,
                'Status': 'Active'
            },
            {
                'Plan Name': 'Enterprise',
                'Description': 'Advanced plan for large operations',
                'Is Free': 'No',
                'Price': 3000,
                'Price Period': 'yearly',
                'Vehicles Limit': 20,
                'Drivers Limit': 9,
                'Routes Limit': 9,
                'Users Limit': 8,
                'Status': 'Active'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        ws['!cols'] = [
            { wch: 18 }, { wch: 30 }, { wch: 10 },
            { wch: 10 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, 'Price_Plans_Import_Template.xlsx');
    };

    const handleImport = async () => {
        if (parsedData.length === 0) return;
        setImporting(true);
        try {
            const res = await fetch(`/api/price-plans/import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ plans: parsedData })
            });
            const data = await res.json();
            if (res.ok) {
                setResult(data);
            } else {
                alert(data.message || "Import failed. Please try again.");
            }
        } catch (err) {
            console.error(err);
            alert("Import failed. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    const matchedCount = parsedData.filter(d => {
        const name = (d.name || d['Plan Name'] || d['Name'] || '').trim().toLowerCase();
        return name && existingNames.has(name);
    }).length;

    const newCount = parsedData.filter(d => {
        const name = (d.name || d['Plan Name'] || d['Name'] || '').trim().toLowerCase();
        return name && !existingNames.has(name);
    }).length;

    if (result) {
        return (
            <div className="mpp-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: '24px' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>Import Completed!</h2>
                    <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '600px', margin: '0 auto 30px' }}>
                        The price plan data has been processed successfully. Existing plans were updated and new ones were created.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', maxWidth: '700px', margin: '0 auto 30px' }}>
                        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#065f46' }}>{result.updatedCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#047857', marginTop: '4px' }}>Plans Updated</div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e40af' }}>{result.createdCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', marginTop: '4px' }}>New Plans Created</div>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#92400e' }}>{(result.errors?.length) || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginTop: '4px' }}>Skipped / Warnings</div>
                        </div>
                    </div>

                    {result.errors && result.errors.length > 0 && (
                        <div style={{ maxWidth: '750px', margin: '0 auto 30px', textAlign: 'left' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', marginBottom: '10px' }}>Details / Warnings:</h4>
                            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
                                {result.errors.map((err, idx) => (
                                    <div key={idx} style={{ fontSize: '13px', color: '#64748b', padding: '6px 0', borderBottom: idx < result.errors.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                        ⚠️ Row {err.row}: {err.message}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onDone}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}
                    >
                        Return to Price Plans
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mpp-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                        onClick={onBack}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Import & Update Price Plans</h1>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>Bulk import new price plans or update existing ones via Excel upload.</p>
                    </div>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px' }}>
                    {/* Instructions Left Column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ℹ️ How it works
                            </h3>
                            <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                                <li>The file must be an Excel sheet (<code>.xlsx</code> or <code>.xls</code>).</li>
                                <li>It matches uploaded rows to <strong>EXISTING</strong> plans in the database by their <strong>Plan Name</strong> (case-insensitive) to update them.</li>
                                <li style={{ fontWeight: '600', color: '#16539C' }}>Unmatched plan names will automatically be created as brand new price plans!</li>
                                <li>Limits, prices, descriptions, and statuses will be mapped and set.</li>
                            </ul>
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {['Plan Name *', 'Description', 'Is Free', 'Price', 'Price Period', 'Vehicles Limit', 'Drivers Limit', 'Routes Limit', 'Users Limit', 'Status'].map((f, i) => (
                                    <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: '6px' }}>
                                        {f}
                                    </span>
                                ))}
                            </div>
                            <p style={{ fontSize: '11.5px', color: '#64748b', marginTop: '10px', margin: '10px 0 0' }}>* <strong>Plan Name</strong> is required. All limits default to -1 (unlimited) if left empty.</p>
                        </div>

                        <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px' }}>Need a starting point? Download our pre-configured format template.</p>
                            <button
                                onClick={downloadTemplate}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Download Sample Template
                            </button>
                        </div>
                    </div>

                    {/* Upload Box Right Column */}
                    <div
                        onDragEnter={handleDrag}
                        onDragOver={handleDrag}
                        onDragLeave={handleDrag}
                        onDrop={handleDrop}
                        style={{
                            background: 'white',
                            borderRadius: '16px',
                            border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`,
                            backgroundColor: dragActive ? '#f0f7ff' : 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '60px 40px',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                            transition: 'all 0.15s',
                            cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('file-upload-input').click()}
                    >
                        <input
                            id="file-upload-input"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: '#eef2ff', color: '#16539C', marginBottom: '20px' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                            Supports .xlsx, .xls formats
                        </span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9', marginBottom: '20px' }}>
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Review Price Plans</h3>
                            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '4px 0 0' }}>
                                Found <strong>{parsedData.length} records</strong>. We will match existing plans by name to update them, and create any new ones.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => { setFile(null); setParsedData([]); }}
                                style={{ padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Reset
                            </button>
                            <button
                                onClick={handleImport}
                                disabled={importing}
                                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)', opacity: importing ? 0.7 : 1 }}
                            >
                                {importing ? 'Processing...' : `Proceed Import (${matchedCount + newCount})`}
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#166534', fontWeight: '600' }}>To Update (Match)</span>
                            <span style={{ fontSize: '18px', color: '#15803d', fontWeight: '800' }}>{matchedCount}</span>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#1e40af', fontWeight: '600' }}>To Create (New)</span>
                            <span style={{ fontSize: '18px', color: '#1d4ed8', fontWeight: '800' }}>{newCount}</span>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto', maxHeight: '350px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Plan Name</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Description</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Price</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Period</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Vehicles</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Operators</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Routes</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Users</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Passengers</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Clients</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Import Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((row, idx) => {
                                    const name = (row.name || row['Plan Name'] || row['Name'] || '').trim();
                                    const desc = row.description || row['Description'] || '';
                                    const price = row.price || row['Price'] || 0;
                                    const period = row.pricePeriod || row['Price Period'] || row['Period'] || '';
                                    const vehicles = row.fleetLimit || row['Fleet Limit'] || row['Fleet'] || row.vehiclesLimit || row['Vehicles Limit'] || row['Vehicles'] || -1;
                                    const operators = row.driversLimit || row['Drivers Limit'] || row['Drivers'] || -1;
                                    const routes = row.routesLimit || row['Routes Limit'] || row['Routes'] || -1;
                                    const users = row.usersLimit || row['Users Limit'] || row['Users'] || -1;
                                    const passengers = row.passengerLimit || row['Passenger Limit'] || row['Passengers'] || -1;
                                    const clients = row.clientsLimit || row['Clients Limit'] || row['Clients'] || -1;
                                    
                                    const isMatch = name && existingNames.has(name.toLowerCase());
                                    const actionText = isMatch ? 'Update Existing' : 'Create New';
                                    const actionBg = isMatch ? '#f0fdf4' : '#eff6ff';
                                    const actionColor = isMatch ? '#166534' : '#1e40af';

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: '600' }}>{name || <span style={{ color: '#ef4444' }}>Missing Name</span>}</td>
                                            <td style={{ padding: '12px 16px', color: '#64748b', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{desc}</td>
                                            <td style={{ padding: '12px 16px' }}>{price}</td>
                                            <td style={{ padding: '12px 16px' }}>{period}</td>
                                            <td style={{ padding: '12px 16px' }}>{vehicles}</td>
                                            <td style={{ padding: '12px 16px' }}>{operators}</td>
                                            <td style={{ padding: '12px 16px' }}>{routes}</td>
                                            <td style={{ padding: '12px 16px' }}>{users}</td>
                                            <td style={{ padding: '12px 16px' }}>{passengers}</td>
                                            <td style={{ padding: '12px 16px' }}>{clients}</td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: '700', padding: '4px 8px', borderRadius: '4px', backgroundColor: actionBg, color: actionColor }}>
                                                    {actionText}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
